
import React from 'react';
import type { Landmark, AnalysisResult, CaptureSequence, Point3D, Model3D, CustomLandmarks, CapturedImage, AnalysisData } from '../types';
import { IDX, IDEAL, SEVERITY_BANDS, MASS_DISTRIBUTION } from '../constants';

// Helper to get configured severity bands (synchronous, uses cached values)
let cachedSeverityBands: typeof SEVERITY_BANDS | null = null;
let severityBandsLoadPromise: Promise<void> | null = null;

async function loadSeverityBands(): Promise<void> {
  if (cachedSeverityBands) return;
  if (severityBandsLoadPromise) return severityBandsLoadPromise;

  severityBandsLoadPromise = (async () => {
    try {
      const { getPostureParams } = await import('@/lib/posture-analysis/rule-params');
      const params = await getPostureParams();
      
      const bands = { ...SEVERITY_BANDS };
      
      if (params.severityBands) {
        Object.keys(params.severityBands).forEach((key) => {
          const typedKey = key as keyof typeof params.severityBands;
          if (params.severityBands?.[typedKey] && Array.isArray(params.severityBands[typedKey])) {
            (bands as any)[typedKey] = params.severityBands[typedKey] as [number, number, number];
          }
        });
      }

      // Handle pelvicShift with configured percentages
      if (params.pelvicShiftPercentages) {
        const [greenPct, yellowPct, redPct] = params.pelvicShiftPercentages;
        bands.pelvicShift = (hipWidthCM: number) => [
          hipWidthCM * (greenPct / 100) || 0.5,
          hipWidthCM * (yellowPct / 100) || 1.0,
          hipWidthCM * (redPct / 100) || 2.0
        ] as [number, number, number];
      }

      cachedSeverityBands = bands;
    } catch (error) {
      logger.error('Failed to load severity bands:', error);
      cachedSeverityBands = SEVERITY_BANDS;
    }
  })();

  return severityBandsLoadPromise;
}
import logger from '@/lib/logging';

// Initialize on module load
loadSeverityBands();

function getSeverityBands(): typeof SEVERITY_BANDS {
  return cachedSeverityBands || SEVERITY_BANDS;
}

export function invalidateSeverityBandsCache(): void {
  cachedSeverityBands = null;
  severityBandsLoadPromise = null;
  loadSeverityBands();
}

// Helper to get configured gauge colors (synchronous, uses cached values)
let cachedGaugeColors: {
  level0: string;
  level1: string;
  level2: string;
  level3: string;
} | null = null;
let gaugeColorsLoadPromise: Promise<void> | null = null;

async function loadGaugeColors(): Promise<void> {
  if (cachedGaugeColors) return;
  if (gaugeColorsLoadPromise) return gaugeColorsLoadPromise;

  gaugeColorsLoadPromise = (async () => {
    try {
      const { getPostureParams } = await import('@/lib/posture-analysis/rule-params');
      const params = await getPostureParams();
      
      cachedGaugeColors = {
        level0: params.gaugeColors?.level0 || '#4ade80',
        level1: params.gaugeColors?.level1 || '#fb923c',
        level2: params.gaugeColors?.level2 || '#ef4444',
        level3: params.gaugeColors?.level3 || '#dc2626',
      };
    } catch (error) {
      logger.error('Failed to load gauge colors:', error);
      cachedGaugeColors = {
        level0: '#4ade80',
        level1: '#fb923c',
        level2: '#ef4444',
        level3: '#dc2626',
      };
    }
  })();

  return gaugeColorsLoadPromise;
}

// Initialize on module load
loadGaugeColors();

export function getGaugeColors(): {
  level0: string;
  level1: string;
  level2: string;
  level3: string;
} {
  return cachedGaugeColors || {
    level0: '#4ade80',
    level1: '#fb923c',
    level2: '#ef4444',
    level3: '#dc2626',
  };
}

export function invalidateGaugeColorsCache(): void {
  cachedGaugeColors = null;
  gaugeColorsLoadPromise = null;
  loadGaugeColors();
}

// UTILS
const rad2deg = (r: number) => r * 180 / Math.PI;
const distPx = (a: Landmark, b: Landmark) => Math.hypot(a.x - b.x, a.y - b.y);
const angleAt = (a: Landmark, b: Landmark, c: Landmark) => {
    if (!a || !b || !c) return NaN;
    const ab = { x: a.x - b.x, y: a.y - b.y }, cb = { x: c.x - b.x, y: c.y - b.y };
    const dot = ab.x * cb.x + ab.y * cb.y;
    const mab = Math.hypot(ab.x, ab.y), mcb = Math.hypot(cb.x, cb.y);
    if (mab === 0 || mcb === 0) return NaN;
    const cos = Math.min(1, Math.max(-1, dot / (mab * mcb)));
    return rad2deg(Math.acos(cos));
};
const horizAngle = (a: Landmark, b: Landmark) => rad2deg(Math.atan2(b.y - a.y, b.x - a.x));
const vertFrom = (a: Landmark, b: Landmark) => rad2deg(Math.atan2(a.x - b.x, b.y - a.y));

export const sev = (value: number | undefined | null, bands: [number, number, number]): number => {
    if (value === undefined || value === null || !isFinite(value)) return 0;
    const x = Math.abs(value);
    if (x < bands[0]) return 0; if (x < bands[1]) return 1; if (x < bands[2]) return 2;
    return 3;
};
export const sevBadge = (lv: number) => {
    const styles = ['bg-status-clear text-accent-fg', 'bg-status-warn text-accent-fg', 'bg-status-warn text-accent-fg', 'bg-status-deviation text-accent-fg'];
    const labels = ['OK', 'Minor', 'Moderate', 'Severe'];
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${styles[lv]}`}>
            {labels[lv]}
        </span>
    );
};
export { fmtLenWithUnit, fmtSignedDeg } from '@/lib/posture-analysis/posture-pic-evaluation';
export { analyzeAnterior, analyzePosterior, analyzeLateral, toAnalysisDataObject } from '@/lib/posture-analysis/posture-pic-evaluation';

/** Inter-aural distance / max(shoulder, hip) width — below this reads as back-of-head compression in 2D. Tuned so real full-body frontals (~0.26) stay Anterior. */
const CORONAL_EAR_TO_BODY_POSTERIOR_MAX = 0.25;
/** Sum of |Δz| on shoulders + hips; above this and with low visDiff, treat as lateral profile (not coronal). Kept above doorway-style frontal z spread (~0.20). */
const Z_DEPTH_LATERAL_MIN = 0.22;

export function detectView(lm: Landmark[], minVisibility = 0.5): CaptureSequence | null {
    if (!lm || lm.length === 0) return null;
    const has = (i: number) => lm[i] && (lm[i].visibility ?? 1) > minVisibility;

    const hasL = has(IDX.L_SHO) && has(IDX.L_HIP) && has(IDX.L_KNEE);
    const hasR = has(IDX.R_SHO) && has(IDX.R_HIP) && has(IDX.R_KNEE);
    const hasBoth = hasL && hasR;

    if (!hasBoth) {
        if (hasL && !hasR) return 'Left';
        if (hasR && !hasL) return 'Right';
        return null;
    }

    const lVisSum = (lm[IDX.L_SHO]?.visibility || 0) + (lm[IDX.L_HIP]?.visibility || 0) + (lm[IDX.L_KNEE]?.visibility || 0);
    const rVisSum = (lm[IDX.R_SHO]?.visibility || 0) + (lm[IDX.R_HIP]?.visibility || 0) + (lm[IDX.R_KNEE]?.visibility || 0);
    const visDiff = Math.abs(lVisSum - rVisSum);

    if (visDiff > 0.3) {
        return lVisSum > rVisSum ? 'Left' : 'Right';
    }

    const lSho = lm[IDX.L_SHO];
    const rSho = lm[IDX.R_SHO];
    const lHip = lm[IDX.L_HIP];
    const rHip = lm[IDX.R_HIP];
    const zDelta = (a: Landmark | undefined, b: Landmark | undefined) =>
        a && b ? Math.abs((a.z ?? 0) - (b.z ?? 0)) : 0;
    const zDepthTorso = zDelta(lSho, rSho) + zDelta(lHip, rHip);
    if (zDepthTorso >= Z_DEPTH_LATERAL_MIN) {
        const lDepth = (lSho?.z ?? 0) + (lHip?.z ?? 0);
        const rDepth = (rSho?.z ?? 0) + (rHip?.z ?? 0);
        return lDepth < rDepth ? 'Left' : 'Right';
    }

    // Coronal (front vs back): use ear separation relative to torso width so wide full-body
    // frames (doorway) are not misclassified by a fixed pixel ear threshold.
    const shoulderW = lSho && rSho ? Math.abs(lSho.x - rSho.x) : 0;
    const hipW = lHip && rHip ? Math.abs(lHip.x - rHip.x) : 0;
    const bodyW = Math.max(shoulderW, hipW, 1e-6);

    if (has(IDX.L_EAR) && has(IDX.R_EAR)) {
        const lEar = lm[IDX.L_EAR]!;
        const rEar = lm[IDX.R_EAR]!;
        const earDist = Math.abs(lEar.x - rEar.x);
        const earToBody = earDist / bodyW;
        if (earToBody < CORONAL_EAR_TO_BODY_POSTERIOR_MAX) {
            return 'Posterior';
        }
        return 'Anterior';
    }

    return 'Anterior';
}

export function tryBuild3DModel(capturedImages: Record<CaptureSequence, CapturedImage | null>): Model3D | null {
    const { Anterior: ap, Posterior: pa, Right: rt, Left: lt } = capturedImages;
    if (!((ap?.status === 'complete' || pa?.status === 'complete') && (rt?.status === 'complete' || lt?.status === 'complete'))) return null;

    const points = Object.values(IDX);
    const out: Record<number, Point3D> = {};

    const originFromPosterior = !(
        ap?.landmarks && ap.landmarks[IDX.L_ANK] && ap.landmarks[IDX.R_ANK]
    ) && !!(pa?.landmarks && pa.landmarks[IDX.L_ANK] && pa.landmarks[IDX.R_ANK]);

    const originAP = (ap?.landmarks && ap.landmarks[IDX.L_ANK] && ap.landmarks[IDX.R_ANK]) ? { x: (ap.landmarks[IDX.L_ANK].x + ap.landmarks[IDX.R_ANK].x) / 2, y: (ap.landmarks[IDX.L_ANK].y + ap.landmarks[IDX.R_ANK].y) / 2, s: ap.cmPerPx! }
        : (pa?.landmarks && pa.landmarks[IDX.L_ANK] && pa.landmarks[IDX.R_ANK]) ? { x: (pa.landmarks[IDX.L_ANK].x + pa.landmarks[IDX.R_ANK].x) / 2, y: (pa.landmarks[IDX.L_ANK].y + pa.landmarks[IDX.R_ANK].y) / 2, s: pa.cmPerPx! } : null;
    const refR = rt?.landmarks && rt.landmarks[IDX.R_ANK] ? { x: rt.landmarks[IDX.R_ANK].x, s: rt.cmPerPx! } : null;
    const refL = lt?.landmarks && lt.landmarks[IDX.L_ANK] ? { x: lt.landmarks[IDX.L_ANK].x, s: lt.cmPerPx! } : null;

    for (const i of points) {
        let X: number | null = null, Y: number | null = null, Zvals: number[] = [];
        if (originAP) {
            const apLm = (ap?.landmarks?.[i]) || (pa?.landmarks?.[i]);
            if (apLm) {
                X = (apLm.x - originAP.x) * originAP.s;
                if (originFromPosterior) X = -X;
                Y = -(apLm.y - originAP.y) * originAP.s;
            }
        }
        if (refR && rt?.landmarks?.[i]) Zvals.push((rt.landmarks[i].x - refR.x) * refR.s);
        if (refL && lt?.landmarks?.[i]) Zvals.push(-(lt.landmarks[i].x - refL.x) * refL.s);
        const Z = Zvals.length ? Zvals.reduce((a, b) => a + b, 0) / Zvals.length : null;
        if (X !== null || Y !== null || Z !== null) out[i] = { x_cm: X, y_cm: Y, z_cm: Z };
    }
    return { points: out, meta: { clientHeightCM: 0, unit: 'cm' } }; // Meta filled later
}

export function estimateCOM(model: Model3D, config?: { massDistribution: typeof MASS_DISTRIBUTION }) {
    if (!model?.points) return null; const P = model.points; const g = (i: number) => P[i] || null;
    
    const validPoint = (p: Point3D | null) => p && p.x_cm !== null && p.y_cm !== null && p.z_cm !== null;
    
    const mid = (a: Point3D | null, b: Point3D | null) => {
        if (!validPoint(a) || !validPoint(b)) return null;
        return { x: (a!.x_cm! + b!.x_cm!) / 2, y: (a!.y_cm! + b!.y_cm!) / 2, z: (a!.z_cm! + b!.z_cm!) / 2 };
    };
    
    const point = (p: Point3D | null) => {
        if (!validPoint(p)) return null;
        return { x: p!.x_cm!, y: p!.y_cm!, z: p!.z_cm! };
    };

    const mass = config?.massDistribution || MASS_DISTRIBUTION;

    const parts = [
        { w: mass.head, p: mid(g(IDX.L_EAR), g(IDX.R_EAR)) },
        { w: mass.trunk, p: mid(g(IDX.L_SHO), g(IDX.R_SHO)) }, // Approximation using mid-shoulder
        { w: mass.armL, p: mid(g(IDX.L_SHO), g(IDX.L_ELB)) }, // Upper arm approximation
        { w: mass.armR, p: mid(g(IDX.R_SHO), g(IDX.R_ELB)) }, // Upper arm approximation
        { w: mass.hipL, p: g(IDX.L_HIP) ? point(g(IDX.L_HIP)) : null },
        { w: mass.hipR, p: g(IDX.R_HIP) ? point(g(IDX.R_HIP)) : null },
        { w: mass.thighL, p: mid(g(IDX.L_HIP), g(IDX.L_KNEE)) },
        { w: mass.thighR, p: mid(g(IDX.R_HIP), g(IDX.R_KNEE)) },
        { w: mass.shankL, p: mid(g(IDX.L_KNEE), g(IDX.L_ANK)) },
        { w: mass.shankR, p: mid(g(IDX.R_KNEE), g(IDX.R_ANK)) },
    ].filter(part => part.p && (part.w || part.w === 0));

    const W = parts.reduce((s, a) => s + (a.w || 0), 0);
    if (W === 0) return null;
    const c = parts.reduce((s, a) => ({ x: s.x + a.p!.x * (a.w||0), y: s.y + a.p!.y * (a.w||0), z: s.z + a.p!.z * (a.w||0) }), { x: 0, y: 0, z: 0 });
    return { x: c.x / W, y: c.y / W, z: c.z / W };
}

export function computeRotation(model: Model3D) {
    if (!model?.points) return null; const P = model.points;
    const pair = (l: number, r: number) => {
        const Lp = P[l], Rp = P[r]; if (!Lp || !Rp || Lp.x_cm === null || Rp.x_cm === null || Lp.z_cm === null || Rp.z_cm === null) return null;
        const span = Math.abs(Rp.x_cm - Lp.x_cm); const dz = Rp.z_cm - Lp.z_cm;
        return { deg: isFinite(span) && span > 0 ? rad2deg(Math.atan2(dz, span)) : NaN };
    };
    const leftEar = P[IDX.L_EAR];
    const rightEar = P[IDX.R_EAR];
    const hasHeadPoints = Boolean(
        leftEar && rightEar &&
        leftEar.y_cm !== null && rightEar.y_cm !== null &&
        leftEar.x_cm !== null && rightEar.x_cm !== null
    );
    const headTiltSigned = hasHeadPoints
        ? rad2deg(Math.atan2((rightEar as any).y_cm - (leftEar as any).y_cm, Math.abs((rightEar as any).x_cm - (leftEar as any).x_cm)))
        : NaN;
    return {
        head: pair(IDX.L_EAR, IDX.R_EAR), shoulder: pair(IDX.L_SHO, IDX.R_SHO), hip: pair(IDX.L_HIP, IDX.R_HIP),
        headTiltSigned
    };
}

import type { ComputePostureDiagnosesOptions } from '@/lib/posture-analysis/posture-pic-evaluation';

export async function classifyPosture(
  analysisData: any,
  clientSex?: string,
  options?: ComputePostureDiagnosesOptions
): Promise<any> {
  // Use the shared library directly for real-time performance in the browser
  const { computePostureDiagnoses } = await import('@/lib/posture-analysis/posture-pic-evaluation');
  return computePostureDiagnoses(analysisData, clientSex as any, options);
}

